import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { Data } from '../data/Data';
import { useLotsCommerciaux } from './lotCommercialContext';
import { useNotificationsManager } from './notificationContext';

const ActualiteContext = createContext();
const ActualiteManagerContext = createContext();

export const ActualiteContextProvider = ({ children }) => {

    const { dismiss, extractErrorMessages, inform } = useNotificationsManager();
    const { idOperationActive } = useLotsCommerciaux();

    const [allActualites, setAllActualites] = useState([]);
    const [loadedIdOperations, setLoadedIdOperations] = useState([]);

    // Chargement différé des images
    const loadImages = useCallback(async () => {
        if (Array.isArray(allActualites)) {
            const allImages = allActualites.map(actualite => actualite.images).flat(1);
            for (let i = 0; i < allImages.length; i++) {
                const image = allImages[i];
                const { rowGUID, fileData } = image;
                if (!fileData) {
                    const response = await Data._getFileData(rowGUID);
                    if (response.codeRetour === 0) {
                        const objet = response.reponseAPIobjet;
                        setAllActualites(prev => {
                            // Mise à jour du filedata de l'image
                            let newActualites = [...prev];
                            const aIndex = prev.findIndex(actualite => actualite.images.some(image => image.rowGUID === rowGUID));
                            if (aIndex >= 0) {
                                const newActualite = { ...prev[aIndex] }; // Copie de l'actualité
                                const newImages = [...newActualite.images]; // Copie de la liste des images
                                const iIndex = newActualite.images.findIndex(image => image.rowGUID === rowGUID); // Index de l'image à modifier
                                const newImage = { ...newImages[iIndex], fileData: objet.fileData }; // Image mise à jour
                                newImages[iIndex] = newImage; // Remplacement de l'image mise à jour
                                newActualite.images = newImages; // Remplacement de la liste des images
                                newActualites[aIndex] = newActualite; // Remplacement de l'actualité mise à jour
                            }
                            return newActualites;
                        });
                    }
                    else extractErrorMessages(response);
                }
            }
        }
        // allActualites ne peut pas être passé dans les dependencies car provoquerait une boucle infinie
        // eslint-disable-next-line
    }, [allActualites.length, extractErrorMessages]);

    useEffect(() => {
        loadImages();
    }, [loadImages]);

    const loadActualites = useCallback((idOperation) => {
        const id = inform("Chargement des actualités...");

        Data._getActualites(idOperation).then(response => {
            if (response.codeRetour === 0) {
                const objet = response.reponseAPIobjet;
                let actualites = objet.actualites || [];
                
                // Suppression des doublons (actualités nationales)
                setAllActualites(prevActualites => {
                    let merged = [...prevActualites];
                    actualites.forEach(actualite => {
                        if (!merged.some(m => m.idActualite === actualite.idActualite))
                            merged.push(actualite);
                    });
                    merged = merged.sort((a, b) => new Date(b.datePublication) - new Date(a.datePublication));

                    return merged;
                });
                setLoadedIdOperations(prevLoadedIdOperations => [...prevLoadedIdOperations, idOperation]);
            }
            else extractErrorMessages(response);
            dismiss(id);
        });
    }, [dismiss, extractErrorMessages, inform]);

    const actualites = useMemo(() => {
        if (!idOperationActive) return [];
        const loaded = loadedIdOperations.includes(idOperationActive);
        if (!loaded) {
            loadActualites(idOperationActive);
            return allActualites.filter(actualite => actualite.nationale);
        }
        else return allActualites.filter(actualite => actualite.nationale || actualite.idOperation === idOperationActive);
    }, [allActualites, idOperationActive, loadActualites, loadedIdOperations]);

    const resetActualites = useCallback(() => {
        setAllActualites([]);
        setLoadedIdOperations([]);
    }, []);

    const value = useMemo(
        () => ({ actualites }),
        [actualites]
    );

    const managerValue = useMemo(
        () => ({ resetActualites }),
        [resetActualites]
    );

    return <ActualiteManagerContext.Provider value={managerValue}>
        <ActualiteContext.Provider value={value}>
            {children}
        </ActualiteContext.Provider>
    </ActualiteManagerContext.Provider>;

};

export const useActualites = () => {
    const data = React.useContext(ActualiteContext);
    if (data === undefined) throw new Error();
    const { actualites } = data;
    return { actualites };
};

export const useActualitesManager = () => {
    const manager = React.useContext(ActualiteManagerContext);
    if (manager === undefined) throw new Error();
    const { resetActualites } = manager;
    return { resetActualites };
};